import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "TarjetaUID";

/**
 * Normaliza textos para comparar nombres de módulos.
 */
const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

/**
 * Normaliza el UID.
 *
 * Ejemplos:
 * 04:A1:B2:C3:D4 => 04A1B2C3D4
 * 04-a1-b2-c3-d4 => 04A1B2C3D4
 */
const normalizarUID = (valor = "") =>
  String(valor)
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Configuración de autorización para Axios.
 */
const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * Obtiene los permisos del módulo desde localStorage.
 */
const obtenerPermisosModulo = () => {
  try {
    const permisosStorage = localStorage.getItem("permisos");
    const usuarioStorage = localStorage.getItem("usuario");
    const userStorage = localStorage.getItem("user");
    const usuariosStorage = localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

    return (
      modulos.find((modulo) => {
        const nombreModulo =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          "";

        return normalizarTexto(nombreModulo) === normalizarTexto(MODULO);
      }) || {}
    );
  } catch (error) {
    console.error("Error leyendo los permisos:", error);
    return {};
  }
};

const RegistrarTarjetaAprendiz = () => {
  const [documentoAprendiz, setDocumentoAprendiz] = useState("");
  const [nombreAprendiz, setNombreAprendiz] = useState("");
  const [tarjetaUID, setTarjetaUID] = useState("");
  const [aprendizId, setAprendizId] = useState("");

  const [verificando, setVerificando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [leyendoNFC, setLeyendoNFC] = useState(false);

  const lectorNFCRef = useRef(null);
  const abortControllerRef = useRef(null);

  const navigate = useNavigate();

  const permisosModulo = useMemo(() => obtenerPermisosModulo(), []);

  const puedeCrear =
    permisosModulo?.puedeCrear === true ||
    permisosModulo?.crear === true;

  const aprendizVerificado = useMemo(
    () => Boolean(aprendizId && nombreAprendiz),
    [aprendizId, nombreAprendiz]
  );

  const nfcDisponible = useMemo(
    () =>
      typeof window !== "undefined" &&
      "NDEFReader" in window,
    []
  );

  const contextoSeguro = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.isSecureContext,
    []
  );

  /**
   * Detiene cualquier lectura NFC activa.
   */
  const detenerLecturaNFC = useCallback(() => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    } catch (error) {
      console.error("Error deteniendo NFC:", error);
    } finally {
      abortControllerRef.current = null;
      lectorNFCRef.current = null;
      setLeyendoNFC(false);
    }
  }, []);

  /**
   * Limpia todos los datos del formulario.
   */
  const limpiarFormulario = useCallback(() => {
    detenerLecturaNFC();

    setDocumentoAprendiz("");
    setNombreAprendiz("");
    setTarjetaUID("");
    setAprendizId("");
  }, [detenerLecturaNFC]);

  /**
   * Regresa al listado de tarjetas.
   */
  const regresarListado = useCallback(() => {
    detenerLecturaNFC();
    navigate("/tarjeta/listartarjetaaprendiz");
  }, [detenerLecturaNFC, navigate]);

  /**
   * Detiene el lector NFC al salir del componente.
   */
  useEffect(() => {
    return () => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } catch (error) {
        console.error("Error cerrando el lector NFC:", error);
      }
    };
  }, []);

  /**
   * Verifica el aprendiz mediante el número de documento.
   */
  const verificarDocumentoAprendiz = useCallback(
    async (documento) => {
      const doc = String(documento || "").trim();

      if (!doc || verificando || guardando) {
        return;
      }

      detenerLecturaNFC();
      setTarjetaUID("");

      try {
        setVerificando(true);

        const response = await axios.get(
          `/api/aprendices/verificar/${encodeURIComponent(doc)}`,
          authConfig()
        );

        if (!response.data?.exists) {
          setNombreAprendiz("");
          setAprendizId("");

          await Swal.fire({
            icon: "error",
            title: "Aprendiz no encontrado",
            text: "El documento ingresado no corresponde a ningún aprendiz.",
          });

          return;
        }

        const nombreCompleto =
          response.data?.nombreCompleto || "";

        const idEncontrado =
          response.data?.aprendizId || "";

        if (!idEncontrado) {
          setNombreAprendiz("");
          setAprendizId("");

          await Swal.fire({
            icon: "warning",
            title: "Información incompleta",
            text: "El aprendiz fue encontrado, pero no se recibió su identificador.",
          });

          return;
        }

        setNombreAprendiz(nombreCompleto);
        setAprendizId(idEncontrado);

        await Swal.fire({
          icon: "success",
          title: "Aprendiz verificado",
          text: nombreCompleto,
          showConfirmButton: false,
          timer: 1400,
        });
      } catch (error) {
        console.error(
          "Error verificando el documento del aprendiz:",
          error
        );

        setNombreAprendiz("");
        setAprendizId("");

        await Swal.fire({
          icon: "error",
          title: "Error al verificar",
          text:
            error.response?.data?.message ||
            "No se pudo verificar el documento del aprendiz.",
        });
      } finally {
        setVerificando(false);
      }
    },
    [
      detenerLecturaNFC,
      guardando,
      verificando,
    ]
  );

  /**
   * Inicia la lectura NFC desde Chrome en Android.
   */
  const iniciarLecturaNFC = useCallback(async () => {
    if (!puedeCrear) {
      await Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text: "No tiene permisos para asignar tarjetas.",
      });

      return;
    }

    if (!aprendizVerificado) {
      await Swal.fire({
        icon: "warning",
        title: "Aprendiz sin verificar",
        text: "Primero debes verificar el documento del aprendiz.",
      });

      return;
    }

    if (!nfcDisponible) {
      await Swal.fire({
        icon: "warning",
        title: "NFC no disponible",
        html: `
          <p>Este navegador no permite usar Web NFC.</p>
          <p class="mb-0">
            Abre la aplicación desde <strong>Google Chrome en Android</strong>
            y verifica que el celular tenga NFC.
          </p>
        `,
      });

      return;
    }

    if (!contextoSeguro) {
      await Swal.fire({
        icon: "warning",
        title: "Se requiere HTTPS",
        html: `
          <p>La lectura NFC solo funciona desde una conexión segura.</p>
          <p class="mb-0">
            La dirección de la aplicación debe comenzar por
            <strong>https://</strong>.
          </p>
        `,
      });

      return;
    }

    try {
      detenerLecturaNFC();

      const abortController = new AbortController();
      const lector = new window.NDEFReader();

      abortControllerRef.current = abortController;
      lectorNFCRef.current = lector;

      lector.addEventListener("reading", async (evento) => {
        const serialNumber = evento.serialNumber || "";
        const uidNormalizado = normalizarUID(serialNumber);

        if (!uidNormalizado) {
          detenerLecturaNFC();

          await Swal.fire({
            icon: "warning",
            title: "UID no disponible",
            html: `
              <p>La tarjeta fue detectada, pero el navegador no entregó su UID.</p>
              <p class="mb-0">
                La tarjeta puede no ser compatible con Web NFC o el celular
                puede estar ocultando el número de serie.
              </p>
            `,
          });

          return;
        }

        setTarjetaUID(uidNormalizado);
        detenerLecturaNFC();

        await Swal.fire({
          icon: "success",
          title: "Tarjeta detectada",
          text: `UID: ${uidNormalizado}`,
          showConfirmButton: false,
          timer: 1800,
        });
      });

      lector.addEventListener("readingerror", async () => {
        detenerLecturaNFC();

        await Swal.fire({
          icon: "error",
          title: "Error de lectura",
          text: "La tarjeta fue detectada, pero el navegador no pudo procesarla.",
        });
      });

      setLeyendoNFC(true);

      await lector.scan({
        signal: abortController.signal,
      });

      await Swal.fire({
        icon: "info",
        title: "Lector NFC activo",
        html: `
          <p>Acerca la tarjeta a la parte posterior del celular.</p>
          <p class="mb-0">
            Mantén esta página abierta mientras se realiza la lectura.
          </p>
        `,
        showConfirmButton: false,
        timer: 2200,
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }

      console.error("Error iniciando NFC:", error);

      detenerLecturaNFC();

      let mensaje = "No se pudo iniciar el lector NFC.";

      switch (error?.name) {
        case "NotAllowedError":
          mensaje =
            "El permiso NFC fue rechazado. Autoriza el acceso desde Chrome.";
          break;

        case "NotSupportedError":
          mensaje =
            "El dispositivo o el navegador no permite esta lectura NFC.";
          break;

        case "NotReadableError":
          mensaje =
            "El NFC está ocupado por otra aplicación o no se encuentra disponible.";
          break;

        case "InvalidStateError":
          mensaje =
            "Ya existe una lectura NFC activa. Detén la lectura e inténtalo nuevamente.";
          break;

        case "SecurityError":
          mensaje =
            "La lectura NFC fue bloqueada. Verifica que la aplicación utilice HTTPS.";
          break;

        default:
          mensaje =
            error?.message ||
            "No se pudo iniciar el lector NFC.";
          break;
      }

      await Swal.fire({
        icon: "error",
        title: "Error NFC",
        text: mensaje,
      });
    }
  }, [
    aprendizVerificado,
    contextoSeguro,
    detenerLecturaNFC,
    nfcDisponible,
    puedeCrear,
  ]);

  /**
   * Guarda la tarjeta asociada al aprendiz.
   */
  const guardarTarjeta = useCallback(async () => {
    if (!puedeCrear) {
      await Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text: "No tiene permisos para asignar tarjetas.",
      });

      return;
    }

    if (!aprendizId) {
      await Swal.fire({
        icon: "warning",
        title: "Aprendiz sin verificar",
        text: "Debes verificar el documento del aprendiz.",
      });

      return;
    }

    const uidNormalizado = normalizarUID(tarjetaUID);

    if (!uidNormalizado) {
      await Swal.fire({
        icon: "warning",
        title: "UID requerido",
        text: "Debes escribir o leer el UID de la tarjeta.",
      });

      return;
    }

    try {
      detenerLecturaNFC();
      setGuardando(true);

      const nuevaAsignacion = {
        aprendiz: aprendizId,
        tarjetaUID: uidNormalizado,
      };

      await axios.post(
        "/api/tarjetas/add",
        nuevaAsignacion,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Tarjeta asignada correctamente",
        text: `UID: ${uidNormalizado}`,
        showConfirmButton: false,
        timer: 1700,
      });

      limpiarFormulario();
      regresarListado();
    } catch (error) {
      console.error("Error asignando la tarjeta:", error);

      await Swal.fire({
        icon: "error",
        title: "Error al asignar la tarjeta",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No se pudo asignar la tarjeta.",
      });
    } finally {
      setGuardando(false);
    }
  }, [
    aprendizId,
    detenerLecturaNFC,
    limpiarFormulario,
    puedeCrear,
    regresarListado,
    tarjetaUID,
  ]);

  /**
   * Envía el formulario.
   */
  const handleSubmit = async (evento) => {
    evento.preventDefault();
    await guardarTarjeta();
  };

  /**
   * Actualiza el documento y elimina la verificación anterior.
   */
  const handleDocumentoChange = (evento) => {
    detenerLecturaNFC();

    setDocumentoAprendiz(evento.target.value);
    setNombreAprendiz("");
    setAprendizId("");
    setTarjetaUID("");
  };

  /**
   * Verifica con la tecla Enter.
   */
  const handleDocumentoKeyDown = (evento) => {
    if (evento.key === "Enter") {
      evento.preventDefault();
      verificarDocumentoAprendiz(documentoAprendiz);
    }
  };

  /**
   * Verifica al abandonar el campo.
   */
  const handleDocumentoBlur = () => {
    if (
      documentoAprendiz.trim() &&
      !aprendizId &&
      !verificando
    ) {
      verificarDocumentoAprendiz(documentoAprendiz);
    }
  };

  /**
   * Permite escribir manualmente el UID.
   */
  const handleTarjetaUIDChange = (evento) => {
    const uidNormalizado = normalizarUID(evento.target.value);
    setTarjetaUID(uidNormalizado);
  };

  const procesoActivo = verificando || guardando;

  const formularioDeshabilitado =
    procesoActivo || !puedeCrear;

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Asignar Tarjeta
                </h5>

                <small className="text-secondary">
                  Verifica el aprendiz y asigna el UID mediante NFC o
                  ingreso manual.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={regresarListado}
                  disabled={procesoActivo}
                  title="Regresar"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2" />
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={limpiarFormulario}
                  disabled={formularioDeshabilitado}
                  title="Limpiar formulario"
                >
                  <i className="fa-solid fa-eraser me-2" />
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-3 p-md-4">
            {!puedeCrear && (
              <div className="alert alert-warning mb-4">
                <i className="fa-solid fa-triangle-exclamation me-2" />
                No tiene permisos para asignar tarjetas en este módulo.
              </div>
            )}

            {!nfcDisponible && (
              <div className="alert alert-info mb-4">
                <i className="fa-solid fa-circle-info me-2" />

                <strong>
                  Lectura NFC no disponible en este navegador.
                </strong>

                <div className="mt-1">
                  Puedes escribir el UID manualmente o abrir la aplicación
                  desde Google Chrome en un celular Android compatible.
                </div>
              </div>
            )}

            {nfcDisponible && !contextoSeguro && (
              <div className="alert alert-warning mb-4">
                <i className="fa-solid fa-lock me-2" />

                La lectura NFC requiere que la aplicación se abra mediante
                HTTPS.
              </div>
            )}

            {procesoActivo && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                  aria-label="Procesando"
                />

                <div className="mt-2">
                  {verificando
                    ? "Verificando aprendiz..."
                    : "Asignando tarjeta..."}
                </div>
              </div>
            )}

            {leyendoNFC && (
              <div className="alert alert-primary mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <i className="fa-solid fa-wifi me-2" />

                    <strong>Lector NFC activo.</strong>

                    <div className="mt-1">
                      Acerca la tarjeta a la parte posterior del celular.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={detenerLecturaNFC}
                  >
                    <i className="fa-solid fa-circle-stop me-2" />
                    Detener
                  </button>
                </div>
              </div>
            )}

            <form
              id="form-tarjeta"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="row g-3 align-items-end">
                {/* Documento del aprendiz */}
                <div className="col-12 col-md-3">
                  <label
                    htmlFor="documentoAprendiz"
                    className="form-label fw-semibold"
                  >
                    Documento Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-id-card" />
                    </span>

                    <input
                      id="documentoAprendiz"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="form-control"
                      placeholder="Ej: 123456789"
                      value={documentoAprendiz}
                      onChange={handleDocumentoChange}
                      onBlur={handleDocumentoBlur}
                      onKeyDown={handleDocumentoKeyDown}
                      required
                      disabled={formularioDeshabilitado}
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        verificarDocumentoAprendiz(documentoAprendiz)
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !documentoAprendiz.trim()
                      }
                      title="Verificar aprendiz"
                    >
                      <i
                        className={`fa-solid ${
                          verificando
                            ? "fa-spinner fa-spin"
                            : "fa-magnifying-glass"
                        } me-2`}
                      />

                      Verificar
                    </button>
                  </div>

                  <div className="form-text">
                    Presiona <strong>Enter</strong> o utiliza{" "}
                    <strong>Verificar</strong>.
                  </div>
                </div>

                {/* Nombre del aprendiz */}
                <div className="col-12 col-md-5">
                  <label
                    htmlFor="nombreAprendiz"
                    className="form-label fw-semibold"
                  >
                    Nombre Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-user" />
                    </span>

                    <input
                      id="nombreAprendiz"
                      type="text"
                      className="form-control"
                      placeholder="Se completa automáticamente"
                      value={nombreAprendiz}
                      readOnly
                      disabled
                    />

                    <span
                      className={`input-group-text ${
                        aprendizVerificado
                          ? "text-success"
                          : "text-secondary"
                      }`}
                    >
                      <i
                        className={`fa-solid ${
                          aprendizVerificado
                            ? "fa-circle-check"
                            : "fa-circle-info"
                        }`}
                      />
                    </span>
                  </div>

                  <div className="form-text">
                    {aprendizVerificado
                      ? "Aprendiz verificado correctamente."
                      : "Primero verifica el documento."}
                  </div>
                </div>

                {/* UID de la tarjeta */}
                <div className="col-12 col-md-4">
                  <label
                    htmlFor="tarjetaUID"
                    className="form-label fw-semibold"
                  >
                    UID de la Tarjeta
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-solid fa-id-badge" />
                    </span>

                    <input
                      id="tarjetaUID"
                      type="text"
                      autoComplete="off"
                      className="form-control text-uppercase"
                      placeholder="Ej: 04A1B2C3D4"
                      value={tarjetaUID}
                      onChange={handleTarjetaUIDChange}
                      required
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        leyendoNFC
                      }
                    />

                    <button
                      type="button"
                      className={
                        leyendoNFC
                          ? "btn btn-outline-danger"
                          : "btn btn-outline-primary"
                      }
                      onClick={
                        leyendoNFC
                          ? detenerLecturaNFC
                          : iniciarLecturaNFC
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        (!nfcDisponible && !leyendoNFC)
                      }
                      title={
                        leyendoNFC
                          ? "Detener lectura NFC"
                          : "Leer tarjeta mediante NFC"
                      }
                    >
                      <i
                        className={`fa-solid ${
                          leyendoNFC
                            ? "fa-circle-stop"
                            : "fa-wifi"
                        } me-2`}
                      />

                      {leyendoNFC ? "Detener" : "Leer NFC"}
                    </button>
                  </div>

                  <div className="form-text">
                    {!aprendizVerificado
                      ? "Primero verifica el aprendiz."
                      : leyendoNFC
                        ? "Acerca la tarjeta a la parte posterior del celular."
                        : nfcDisponible
                          ? "Escribe el UID o utiliza el botón Leer NFC."
                          : "Ingresa manualmente el UID de la tarjeta."}
                  </div>
                </div>

                {/* Botones del formulario */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={regresarListado}
                      disabled={procesoActivo}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2" />
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      className="btn btn-dark"
                      disabled={
                        formularioDeshabilitado ||
                        leyendoNFC ||
                        !aprendizVerificado ||
                        !normalizarUID(tarjetaUID)
                      }
                      title={
                        puedeCrear
                          ? "Asignar tarjeta"
                          : "Sin permiso para asignar tarjeta"
                      }
                    >
                      <i
                        className={`fa-solid ${
                          guardando
                            ? "fa-spinner fa-spin"
                            : "fa-link"
                        } me-2`}
                      />

                      {guardando
                        ? "Asignando..."
                        : "Asignar Tarjeta"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <hr className="my-4" />

            <div className="small text-secondary">
              <i className="fa-solid fa-circle-info me-2" />

              Para leer NFC debes usar Google Chrome en Android, activar
              el NFC del celular y abrir la aplicación mediante HTTPS.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarTarjetaAprendiz;